/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import IconFont from '$/components/IconFont';
import type { V3CanvasHomeCaseItem, V3CanvasHomeCategoryInfo } from '$/domains/caseV3/type';
import { useCardExpo } from '@/hooks/useCardExpo';
import { Popover } from 'antd';
import { handleImageUrl } from '$/utils/image';
import styles from './index.module.scss';

type Props = {
  className?: string;
  homeInputCaseList: V3CanvasHomeCaseItem[];
  selectedCategory: V3CanvasHomeCategoryInfo | null;
  onPromptClick?: (item: V3CanvasHomeCaseItem) => void;
};

const VISIBLE_COUNT = 4;

interface CaseCardProps {
  item: V3CanvasHomeCaseItem;
  positionId: number;
  tag: string;
  onPromptClick?: (item: V3CanvasHomeCaseItem) => void;
}

/** 监听页面滚动并关闭已打开的案例预览，避免 hover 浮层跟随锚点移动。 */
function useClosePopoverOnScroll(open: boolean, setOpen: React.Dispatch<React.SetStateAction<boolean>>) {
  useEffect(() => {
    if (!open) return undefined;

    const closeOnScroll = () => {
      setOpen(false);
    };

    document.addEventListener('scroll', closeOnScroll, true);
    window.visualViewport?.addEventListener('scroll', closeOnScroll);

    return () => {
      document.removeEventListener('scroll', closeOnScroll, true);
      window.visualViewport?.removeEventListener('scroll', closeOnScroll);
    };
  }, [open, setOpen]);
}

/**
 * 单个案例卡片，包含曝光埋点与点击逻辑。
 */
function CaseCard({
  item, positionId, tag, onPromptClick,
}: CaseCardProps) {
  const content = item.prompt || item.desc;
  const [cardWidth, setCardWidth] = useState<number>();
  const [popoverOpen, setPopoverOpen] = useState(false);
  useClosePopoverOnScroll(popoverOpen, setPopoverOpen);

  // 卡片曝光埋点
  const cardRef = useCardExpo<HTMLDivElement>({
    onExpo: () => {
      window.mtstat?.track('roboneo_callword_card_show', {
        tag,
        card_id: item.case_id,
        position_id: positionId,
        text: item.title,
        agent_type: 'agent_teams',
      });
    },
    isActive: true,
  });

  // Popover 渲染在 portal 中，需同步触发卡片的实际自适应宽度。
  useEffect(() => {
    const cardElement = cardRef.current;
    if (!cardElement) return;

    const updateCardWidth = () => {
      setCardWidth(cardElement.getBoundingClientRect().width);
    };

    updateCardWidth();

    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(updateCardWidth);
    resizeObserver.observe(cardElement);

    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
  }, [cardRef]);

  /** 处理热门 prompt 卡片点击，记录选择并触发外部回调。 */
  const handleCardClick = () => {
    // 卡片点击埋点
    window.mtstat?.track('roboneo_callword_card_click', {
      tag,
      card_id: item.case_id,
      position_id: positionId,
      text: item.title,
      agent_type: 'agent_teams',
    });

    onPromptClick?.(item);
  };

  return (
    <Popover
      trigger="hover"
      placement="bottom"
      open={popoverOpen}
      onOpenChange={setPopoverOpen}
      overlayClassName={classNames('resetPopoverAndArrow', styles.casePreviewPopover)}
      content={(
        <div className={styles.popoverCantiner} style={{ width: cardWidth }}>
          {item.work_media.type === 'video' ? (
            <div className={styles['inline-box-popover']}>
              <video
                src={item.work_media.url}
                className={styles['inline-box-popover-video']}
                playsInline
                autoPlay
                muted
                loop
                controls={false}
                controlsList="nodownload nofullscreen noremoteplayback"
                disablePictureInPicture
              >
                <track kind="captions" />
              </video>
            </div>
          ) : (
            <img className={styles.popoverVideo} src={handleImageUrl(item.work_media.url || item.work_media.cover, { size: 360 })} alt="" loading="lazy" />
          )}
        </div>
      )}
    >
      <div
        ref={cardRef}
        className={styles.caseCard}
        onClick={handleCardClick}
      >
        <span className={styles.caseImageWrap}>
          {item.cover_media.cover && <img className={styles.caseImage} src={handleImageUrl(item.cover_media.url, { size: 56, needFormat: false })} alt="" loading="lazy" />}
        </span>
        <span className={styles.caseText}>
          <span className={styles.caseTitle}>{item.title}</span>
          <span className={styles.casePrompt}>{content}</span>
        </span>
        <span className={styles.caseHoverContent}>
          <span className={styles.caseHoverText}>{item.desc}</span>
          <IconFont name="arrowCornerRightUpBold" fontSize={12} className={styles.enterIcon} />
        </span>
      </div>
    </Popover>
  );
}

/**
 * 渲染首页首屏输入框下方的灵感案例横向轮换模块。
 * 数据来源于 store 中扁平案例列表，并按当前选中分类派生展示。
 */
function HotPromptCaseList({
  className,
  homeInputCaseList,
  selectedCategory,
  onPromptClick,
}: Props) {
  const [startIndex, setStartIndex] = useState(0);
  const selectedCategoryId = selectedCategory?.category_id;

  /** 切换服务端分类时从第一条案例重新开始展示。 */
  useEffect(() => {
    setStartIndex(0);
  }, [selectedCategoryId]);

  /** 从扁平案例列表中派生当前分类的展示案例。 */
  const selectedCaseList = useMemo(() => {
    if (selectedCategoryId == null) return [];
    return homeInputCaseList.filter((item) => item.category_id === selectedCategoryId);
  }, [homeInputCaseList, selectedCategoryId]);

  /** 从扁平分类列表中还原当前分类名称，用于埋点。 */
  const selectedCategoryName = useMemo(() => {
    if (selectedCategoryId == null) return '';
    return selectedCategory?.name || '';
  }, [selectedCategory, selectedCategoryId]);

  /** 根据当前起点生成固定数量的可见案例，保持布局宽度稳定。 */
  const visibleItems = useMemo(() => {
    if (!selectedCaseList.length) return [];
    return Array.from({ length: Math.min(VISIBLE_COUNT, selectedCaseList.length) }, (_, index) => {
      return selectedCaseList[(startIndex + index) % selectedCaseList.length];
    });
  }, [selectedCaseList, startIndex]);

  const canMovePrev = startIndex > 0;
  const canMoveNext = startIndex + VISIBLE_COUNT < selectedCaseList.length;

  /** 按指定方向移动可见案例起点（不循环）。 */
  const moveBy = (offset: number) => {
    if (!selectedCaseList.length) return;
    setStartIndex((current) => {
      const next = current + offset;
      return Math.max(0, Math.min(next, selectedCaseList.length - VISIBLE_COUNT));
    });
  };

  // 如果没有选中分类或没有数据，不显示
  if (!selectedCategory || selectedCaseList.length === 0) {
    return null;
  }

  return (
    <div className={classNames(styles.HotPromptCaseList, className)}>
      <div className={styles.cardList}>
        {visibleItems.map((item, index) => (
          <CaseCard
            key={item.case_id}
            item={item}
            positionId={(startIndex + index) % selectedCaseList.length + 1}
            tag={selectedCategoryName}
            onPromptClick={onPromptClick}
          />
        ))}
      </div>

      {selectedCaseList.length > VISIBLE_COUNT && (
        <div className={styles.controls}>
          <div className={styles.shuffleButton} />

          <div className={styles.pageButtons}>
            <div
              className={classNames(styles.iconButton, { [styles.iconButtonDisabled]: !canMovePrev })}
              onClick={() => canMovePrev && moveBy(-1)}
              aria-label="上一组案例"
            >
              <IconFont name="chevronLeftBlack" fontSize={12} />
            </div>
            <div
              className={classNames(styles.iconButton, { [styles.iconButtonDisabled]: !canMoveNext })}
              onClick={() => canMoveNext && moveBy(1)}
              aria-label="下一组案例"
            >
              <IconFont name="chevronRightBlack" fontSize={12} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(HotPromptCaseList);
